// Updated import
import * as React from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';

import {
  AppBar,
  Box,
  Button,
  Container,
  IconButton,
  Theme,
  Toolbar,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid';

import Facebook from '../icons/Facebook.webp';
import Linkedin from '../icons/LinkedIn.webp';
import Twitter from '../icons/Twitter.webp';
import {
  trackClickButtonNewCampaign,
  trackClickSocialHeader,
  trackClickMyCampaigns,
} from '../utils/analytics/analytics';
import { SocialPlatform } from '../utils/analytics/events';
import { trackSentryError } from '../utils/analytics/sentry';
import { createNewCampaign, handleApiError as handleApiErrorUtil } from '../utils/api';
import { useFirebaseLoginState } from '../utils/hooks';
import { resetInputDataSection } from '../utils/state';
import { RootState } from '../utils/store';
import { GoogleAuthProfilePicButton } from './minicomponents/GoogleAuthProfilePicButton';
import { GradientButton } from './minicomponents/GradientButton';
import { RequestsCounter } from './minicomponents/RequestsCounter';
import { IconAddCircle, IconCampaigns } from './minicomponents/icons';
import AlertPopup from './popups/AlertPopup';
import LoadingPopup from './popups/LoadingPopup';
import NewCampaignPopup from './popups/NewCampaignPopup';
import { PricingPopupNonAuthUsers } from './popups/PricingPopupNonAuthUsers';
import UpgradePopup from './popups/UpgradePopup';

const HeaderButton = styled(Button)<{
  component?: React.ElementType;
  href?: string;
  target?: string;
  rel?: string;
  to?: string;
}>(({ theme }) => ({
  margin: theme.spacing(2, 0),
  padding: theme.spacing(0, 2),
  '&:hover': {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
  },
}));

function SocialIconButtonComponent({
  icon,
  url,
  callback,
}: {
  icon: any;
  url: string;
  callback?: () => void;
}) {
  return (
    <IconButton
      sx={{
        width: 36,
        height: 36,
        '&:hover': {
          bgcolor: 'primary.light',
        },
      }}
      onClick={() => {
        if (callback) {
          callback();
        }
        window.open(url);
      }}
    >
      <img src={icon} alt="icon" style={{ width: 24, height: 24 }} />
    </IconButton>
  );
}

function BlogMenuItem() {
  const isTabletUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('tablet'));
  const { t } = useTranslation();

  return (
    <HeaderButton
      key={'Blog'}
      component="a"
      href="/blog"
      target="_blank"
      rel="noopener noreferrer"
    >
      {isTabletUp ? (
        t('website.header.blog')
      ) : (
        <strong>{t('website.header.blog')}</strong>
      )}
    </HeaderButton>
  );
}

export default function Header() {
  const theme = useTheme();

  const isLaptopUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('laptop'));

  const { t } = useTranslation();
  const { isRegistered, userId } = useFirebaseLoginState();
  const state = useSelector((state: RootState) => state.appState);

  const [isUpgradePopupOpen, setUpgradePopupOpen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const isCampaignsScreen =
    location.pathname.includes('/app/campaigns') &&
    location.pathname == '/app/campaigns';
  const isCampaignDetailsScreen =
    location.pathname.startsWith('/app/campaigns/') &&
    location.pathname !== '/app/campaigns';

  const [isNewCampaignPopupOpen, setNewCampaignPopupOpen] = useState(false);
  const [isLoadingProgressOpen, setLoadingProgressOpen] = useState(false);
  const [isAlertPopupOpen, setAlertPopupOpen] = useState(false);
  const [isPricingPopupOpen, setPricingPopupOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  const dispatch = useDispatch();

  const handleNewCampaignOpenPopup = () => {
    setNewCampaignPopupOpen(true);
  };

  const handleNewCampaignClosePopup = () => {
    setNewCampaignPopupOpen(false);
  };

  const handleAlertClosePopup = () => {
    setAlertPopupOpen(false);
  };

  const onCreateNewCampaignButtonHandler = () => {
    trackClickButtonNewCampaign('header');
    handleNewCampaignOpenPopup();
  };

  const handleError = useCallback((error: unknown) => {
    trackSentryError('Error in Header', { error });
    handleApiErrorUtil({
      error,
      onSetUpgradePopupOpen: setUpgradePopupOpen,
      onSetErrorMessage: (message: string) => {
        setAlertMessage(message);
        setAlertPopupOpen(true);
      },
      onSetErrorAlertOpen: (isOpen) => {
        if (!isOpen) {
          setAlertMessage('');
        }
      },
    });
  }, []);

  const handleCreateCampaign = React.useCallback(
    async (params: { url: string; prompt: string; mode: string }) => {
      setNewCampaignPopupOpen(false);
      setLoadingProgressOpen(true);
      try {
        if (!userId) throw new Error('User is not signed in');
        const newCampaignId = await createNewCampaign(
          userId,
          params.url,
          params.prompt,
          state.creativity,
          state.outputLanguage,
          state.session
        );
        dispatch(resetInputDataSection());
        navigate(`/app/campaigns/${newCampaignId}${location.search}`);
      } catch (error) {
        trackSentryError('Error creating campaign', { error });
        console.error('Error creating campaign:', error);
        if (error instanceof Error && error.message.toLowerCase().includes('url')) {
          // If it's a URL-related error, reopen the popup
          setLoadingProgressOpen(false);
          setNewCampaignPopupOpen(true);
        }
        handleError(error);
      } finally {
        setLoadingProgressOpen(false);
      }
    },
    [
      userId,
      state.creativity,
      state.outputLanguage,
      state.session,
      dispatch,
      navigate,
      handleError,
    ]
  );

  function handleAlertPopupSubmit() {
    if (alertMessage === 'Subscription required to create more campaigns') {
      navigate('/app/profile/subscription');
    } else {
      handleNewCampaignOpenPopup();
    }
    setAlertPopupOpen(false);
  }

  function getSocialIcons() {
    return (
      isLaptopUp && (
        <Box alignSelf="center" mr={2}>
          <SocialIconButtonComponent
            icon={Linkedin}
            callback={() => trackClickSocialHeader(SocialPlatform.LINKEDIN)}
            url={'https://www.linkedin.com/company/aiter-io'}
          />
          <SocialIconButtonComponent
            icon={Twitter}
            callback={() => trackClickSocialHeader(SocialPlatform.TWITTER)}
            url={'https://twitter.com/aiter_io'}
          />
          <SocialIconButtonComponent
            icon={Facebook}
            callback={() => trackClickSocialHeader(SocialPlatform.FACEBOOK)}
            url={'https://www.facebook.com/aiter.tool/'}
          />
        </Box>
      )
    );
  }

  function getLogo() {
    return (
      <Typography
        variant="h1"
        onClick={() => {
          navigate(`/app/campaigns${location.search}`);
        }}
        sx={{
          background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          textFillColor: 'transparent',
          cursor: 'pointer',
          fontFamily: 'Roboto',
          position: 'relative',
          top: '-1px',
        }}
      >
        aiter
      </Typography>
    );
  }

  function showPricingPopup() {
    setPricingPopupOpen(true);
  }

  function getDesktopView() {
    return (
      <Grid container size={12} sx={{ width: '100%' }}>
        <Grid alignSelf={'center'}>{getLogo()}</Grid>
        <Grid container size="grow" justifyContent="right" gap={1}>
          <Grid
            container
            alignSelf="center"
            justifyContent="left"
            size={'grow'}
            sx={{ mr: isLaptopUp ? 0 : -12, ml: 8 }}
            gap={1}
          >
            <Grid>
              <BlogMenuItem />
            </Grid>
            <Grid>
              {isRegistered ? (
                <HeaderButton
                  key={'pricing'}
                  component={RouterLink}
                  to="/app/profile/subscription"
                >
                  {t('website.header.pricing')}
                </HeaderButton>
              ) : (
                <HeaderButton key={'pricing'} onClick={showPricingPopup}>
                  {t('website.header.pricing')}
                </HeaderButton>
              )}
            </Grid>
          </Grid>
          {isCampaignDetailsScreen && (
            <Grid alignSelf="center" justifyContent="left" sx={{ mr: 1 }}>
              <GradientButton
                version="outlined"
                buttonText={t('app.buttons.newCampaign')}
                iconLeft={<IconAddCircle />}
                onSubmitButtonHandler={onCreateNewCampaignButtonHandler}
              />
            </Grid>
          )}
          {!isRegistered && (
            <Grid alignSelf="center" textAlign="center" sx={{ ml: 2 }}>
              {getSocialIcons()}
            </Grid>
          )}
          {isRegistered && !isCampaignsScreen && (
            <Grid alignSelf="center" justifyContent="left">
              <HeaderButton
                component={RouterLink}
                to="/app/campaigns"
                onClick={() => trackClickMyCampaigns()}
                sx={{
                  '&:hover': { color: 'primary.main' },
                }}
              >
                <IconCampaigns
                  sx={{
                    mr: 1,
                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                />
                {t('app.buttons.myCampaigns')}
              </HeaderButton>
            </Grid>
          )}
          <Grid alignSelf="center" textAlign="center">
            {isRegistered && <RequestsCounter />}
          </Grid>
          <Grid alignSelf={'center'}>
            <GoogleAuthProfilePicButton />
          </Grid>
        </Grid>
      </Grid>
    );
  }

  return (
    <AppBar position="sticky" sx={{ bgcolor: 'white' }} elevation={1}>
      <Container maxWidth="desktop" sx={{ px: '16px! important' }}>
        <Toolbar disableGutters style={{ height: 64 }}>
          {/* Quick & dirty fix to show the desktop UI on mobile */}
          {/* {isTabletUp ? getDesktopView() : getMobileView()} */}
          {getDesktopView()}
        </Toolbar>
      </Container>
      <NewCampaignPopup
        open={isNewCampaignPopupOpen}
        onClose={handleNewCampaignClosePopup}
        onSubmit={handleCreateCampaign}
        location="header"
      />
      <LoadingPopup open={isLoadingProgressOpen} />
      <AlertPopup
        open={isAlertPopupOpen}
        onClose={handleAlertClosePopup}
        onSubmit={handleAlertPopupSubmit}
        buttonText={t('app.buttons.ok')}
        alertText={alertMessage}
        title={t('app.popups.error.title')}
      />
      <PricingPopupNonAuthUsers
        open={isPricingPopupOpen}
        onClose={() => setPricingPopupOpen(false)}
      />
      <UpgradePopup
        open={isUpgradePopupOpen}
        onClose={() => setUpgradePopupOpen(false)}
        onError={handleError}
      />
    </AppBar>
  );
}
